import {gql} from 'types/__generated__';

/**
 * Every role of an organisation: the built-ins (Limited user, User, Admin - and the retired
 * "Organisation owner" role, shown as Admin with assignable = false) plus its custom roles.
 *
 * System admins can read any organisation's roles this way. (The old getPermissionGroups query,
 * removed in the 2026-09 permissions API sweep, answered for the caller's *selected*
 * organisation, which an admin login does not have, so it only ever listed the built-ins.)
 */
export const ORGANISATION_ROLES_QUERY = gql(`
  query OrganisationRoles($id: UUID!) {
    organisation(id: $id) {
      id
      roles {
        id
        name
        description
        builtIn
        assignable
        grantableByMe
        permissionIds
      }
    }
  }
`);

/** The roles the current caller may hand out - what every role picker should offer. */
export function grantableRoles<T extends {grantableByMe: boolean}>(roles: readonly T[] | null | undefined): T[] {
  return (roles ?? []).filter((role) => role.grantableByMe);
}

/** What the member-row gates need to know about a member. */
export interface MemberGate {
  isAccountOwner: boolean;
  role: {membersChangeableByMe: boolean} | null | undefined;
}

export const ACCOUNT_OWNER_REMOVE_REASON = 'The account owner cannot be removed - transfer ownership first';
export const ROLE_ABOVE_CALLER_REASON = "This member's role is above your own";
export const ACCOUNT_OWNER_ROLE_REASON = "Only CWAY staff can change the account owner's role";

/**
 * Why the caller may not change this member's role, or null when they may. The server requires
 * the member's current role to be within the caller's own (that is `role.membersChangeableByMe`),
 * and lets only staff touch the account owner's role. Staff bypass containment.
 *
 * Not `role.grantableByMe`: that answers whether the caller may hand the role out, and the retired
 * "Organisation owner" role is never grantable - yet it counts as Admin, so admins may demote it.
 */
export function roleChangeDisabledReason(member: MemberGate, isSystemAdmin: boolean): string | null {
  if (isSystemAdmin) return null;
  if (member.isAccountOwner) return ACCOUNT_OWNER_ROLE_REASON;
  if (!member.role?.membersChangeableByMe) return ROLE_ABOVE_CALLER_REASON;
  return null;
}

/**
 * Why the caller may not remove this member from the organisation, or null when they may.
 * Nobody - staff included - removes the account owner: ownership is transferred first.
 * Otherwise the same containment rule as for changing the role.
 */
export function removeMemberDisabledReason(member: MemberGate, isSystemAdmin: boolean): string | null {
  if (member.isAccountOwner) return ACCOUNT_OWNER_REMOVE_REASON;
  if (isSystemAdmin) return null;
  if (!member.role?.membersChangeableByMe) return ROLE_ABOVE_CALLER_REASON;
  return null;
}
